import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BatteryCharging,
  BrainCircuit,
  BriefcaseBusiness,
  HeartHandshake,
  MapPinned,
  MessageCircleMore,
  MoonStar,
  Repeat2,
  ShieldCheck,
  Sparkles,
  Users,
  Waves,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import { Reveal } from "@/components/landing/Shared";

const lifeAreas = [
  {
    icon: HeartHandshake,
    title: "Connection and relationships",
    body: "Recognize what happens inside you before you withdraw, become reactive, over-explain, shut down, or reach for support. That awareness creates more room to communicate what you need while a moment is still manageable.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Focus and productivity",
    body: "See whether pressure tends to sharpen your attention, scatter it, lock it onto one problem, or make it difficult to mobilize. This helps explain why the same task can feel accessible one day and unusually demanding on another.",
  },
  {
    icon: Users,
    title: "Socializing and participation",
    body: "Understand how being alone, supported, observed, compared, or part of a group changes your energy, control, and recovery. You can make clearer choices about the setting in which you function best.",
  },
  {
    icon: MoonStar,
    title: "Sleep, anxiety, and recovery",
    body: "Connect sleep quality, usable energy, activation, mental urgency, and the time it takes to settle after stress. The assessment shows how recovery resources may be shaping the rest of your response.",
  },
];

const assessmentAreas = [
  ["01", "Recent change", "What has shifted, when it began, what has helped, and what has added pressure."],
  ["02", "Mobilization", "How early you notice a change, how easily your system activates, and how strong the response becomes."],
  ["03", "Control and recovery", "How much choice remains under pressure, how attention is affected, and how your system returns."],
  ["04", "How it appears", "The emotions, body signals, thinking patterns, and reaction directions that travel together."],
  ["05", "Environment", "Which settings add demand, which settings support you, and how much control you have over them."],
  ["06", "Connection", "How social settings, support, coaching, evaluation, and conflict change the response."],
  ["07", "Physical capacity", "What your body can do comfortably, competently, and recover from with your current load."],
  ["08", "Recovery resources", "How sleep, nourishment, hydration, routine, and lower-demand time support usable capacity."],
  ["09", "Activity and recreation", "The movement, exercise, sport, and familiar activities already available in your life."],
];

const resultAreas = [
  {
    icon: BrainCircuit,
    title: "Your nervous-system pattern",
    body: "A clear explanation of how your system may organize activation, energy, attention, emotion, action, control, and recovery under current conditions.",
  },
  {
    icon: ShieldCheck,
    title: "Why the pattern may be happening",
    body: "A human explanation of the protective, learned, and context-dependent influences that may be shaping the way your system responds now.",
  },
  {
    icon: Waves,
    title: "How it reaches daily life",
    body: "An in-depth review of how the pattern may appear across your body, thinking, behaviour, relationships, productivity, social settings, sleep, and recovery.",
  },
  {
    icon: MapPinned,
    title: "What is adding demand—and helping",
    body: "A personalized view of the environments, routines, social conditions, activities, and recovery resources that may be increasing load or already supporting you.",
  },
  {
    icon: BatteryCharging,
    title: "Your starting direction",
    body: "Priorities for what your nervous system may need first, how to begin, what to build toward, and which strengths or supports should remain in place.",
  },
  {
    icon: Repeat2,
    title: "Why adaptation is possible",
    body: "A practical explanation of how repeated, matched physical input and completed recovery experiences can help create a more flexible response over time.",
  },
];

const AssessmentOverview = () => (
  <PageShell>
    <section className="relative overflow-hidden pb-20 pt-20 sm:pb-28 sm:pt-28">
      <div className="pointer-events-none absolute -top-56 left-1/2 h-[920px] w-[920px] -translate-x-1/2 opacity-60 forr-radial-teal" />
      <div className="relative mx-auto max-w-[1180px] px-6 sm:px-10 lg:px-16">
        <Reveal>
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] forr-text-mute">
            <Link to="/">Home</Link><span>›</span><span>Adaptive Assessment™</span>
          </nav>
          <div className="mt-10 grid items-end gap-10 lg:grid-cols-[1.35fr_0.65fr]">
            <div>
              <div className="inline-flex items-center gap-3"><span className="forr-hairline" /><span className="forr-overline">Adaptive Assessment™ · Available now</span></div>
              <h1 className="mt-6 max-w-4xl font-display text-[44px] font-medium leading-[1.02] tracking-[-0.04em] forr-text-ink sm:text-6xl lg:text-7xl">
                Understand the response beneath how you feel, think, connect, and act.
              </h1>
              <p className="mt-7 max-w-3xl text-lg font-light leading-relaxed forr-text-soft sm:text-xl">
                The Adaptive Assessment gives you an in-depth review of how your nervous system responds to stress, how much choice remains when the response is strong, what affects your recovery, and why the same patterns may keep appearing across daily life.
              </p>
            </div>
            <div className="rounded-[28px] p-7 forr-panel-action sm:p-8">
              <Sparkles size={24} className="forr-text-teal-deep" />
              <h2 className="mt-5 font-display text-2xl forr-text-ink">You receive more than a score.</h2>
              <p className="mt-3 text-sm leading-relaxed forr-text-soft">
                Your responses are organized into a personal nervous-system profile that explains the pattern, what may be influencing it, how it can show up, and where to begin.
              </p>
              <Link to="/assessment/start" data-testid="assessment-overview-start" className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold forr-btn-primary">
                Take the assessment <ArrowRight size={16} />
              </Link>
              <Link to="/coaching" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold forr-text-teal-deep">
                Explore 1-to-1 coaching <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-16 sm:py-24 forr-canvas-warm">
      <div className="mx-auto max-w-[1120px] px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="max-w-4xl">
            <div className="forr-overline">Why understanding yourself matters</div>
            <h2 className="mt-4 font-display text-4xl tracking-[-0.03em] forr-text-ink sm:text-5xl">Your reactions make more sense when you can see the system producing them.</h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed forr-text-soft sm:text-lg">
              <p>
                Human to human, it is hard to change something you can only see after it has already happened. You may know that you become anxious, lose focus, pull away, get irritable, overwork, or struggle to settle—but still not understand what changed inside you first.
              </p>
              <p>
                Your nervous system is constantly deciding how much energy to create, where attention should go, whether to approach or avoid, how urgently to act, and when it is safe to recover. Those decisions affect the version of you that arrives in a conversation, a workday, a social setting, a workout, or the hours before sleep.
              </p>
              <p className="font-medium forr-text-ink">
                Understanding that pattern replaces confusion with context. It gives you language for what is happening, earlier recognition of the shift, and a clearer starting point for responding differently.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {lifeAreas.map((item, index) => {
            const Icon = item.icon;
            return (
              <Reveal key={item.title} delay={index * 0.05}>
                <article className="h-full rounded-[24px] p-6 forr-surface sm:p-7" style={{ border: "1px solid var(--forr-line-soft)" }}>
                  <Icon size={22} className="forr-text-teal-deep" />
                  <h3 className="mt-5 font-display text-2xl forr-text-ink">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed forr-text-soft">{item.body}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    <section className="py-16 sm:py-24">
      <div className="mx-auto max-w-[1120px] px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="max-w-3xl">
            <div className="forr-overline">What the assessment looks at</div>
            <h2 className="mt-4 font-display text-4xl tracking-[-0.03em] forr-text-ink sm:text-5xl">The full response—not one symptom in isolation.</h2>
            <p className="mt-5 text-base leading-relaxed forr-text-soft">
              Stress is not only a feeling. It can change energy, attention, muscle tension, breathing, urgency, behaviour, social comfort, sleep, and the time it takes to feel like yourself again. The assessment separates those parts so your final review can put them back together in a way that is specific to you.
            </p>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-3 lg:grid-cols-3">
          {assessmentAreas.map(([number, title, body], index) => (
            <Reveal key={number} delay={(index % 3) * 0.04}>
              <article className="h-full rounded-[22px] p-6 forr-surface" style={{ border: "1px solid var(--forr-line-soft)" }}>
                <div className="forr-overline">{number}</div>
                <h3 className="mt-3 font-display text-xl forr-text-ink">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed forr-text-soft">{body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 sm:py-24 forr-canvas-warm">
      <div className="mx-auto max-w-[1120px] px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:items-start">
            <div className="lg:sticky lg:top-32">
              <div className="forr-overline">Your in-depth results</div>
              <h2 className="mt-4 font-display text-4xl tracking-[-0.03em] forr-text-ink sm:text-5xl">A review you can recognize yourself in.</h2>
              <p className="mt-5 text-base leading-relaxed forr-text-soft">
                Your final profile connects the individual answers into one understandable explanation. It is built to help you describe yourself more clearly—to yourself and, when useful, to the people you live, work, train, or connect with.
              </p>
              <div className="mt-7 flex items-start gap-3 rounded-[20px] p-5 forr-panel-information">
                <MessageCircleMore size={20} className="mt-0.5 shrink-0 forr-text-teal-deep" />
                <p className="text-sm leading-relaxed forr-text-ink">You can return to your saved results and use the same profile with APOS Companion when access opens.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {resultAreas.map((item, index) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="rounded-[24px] bg-white/80 p-6" style={{ border: "1px solid var(--forr-line-soft)" }}>
                    <div className="flex items-center justify-between gap-4">
                      <span className="forr-overline">0{index + 1}</span>
                      <Icon size={21} className="forr-text-teal-deep" />
                    </div>
                    <h3 className="mt-5 font-display text-2xl forr-text-ink">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed forr-text-soft">{item.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-16 sm:py-24">
      <div className="mx-auto max-w-[980px] px-6 sm:px-10">
        <Reveal>
          <div className="rounded-[32px] p-8 text-center forr-panel-action sm:p-12">
            <div className="forr-overline">Begin when you are ready</div>
            <h2 className="mx-auto mt-4 max-w-3xl font-display text-4xl tracking-[-0.03em] forr-text-ink sm:text-5xl">See the pattern. Understand the why. Know where to begin.</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed forr-text-soft">Create your private assessment account, complete the questions, and receive your personalized Adaptive Physiology review.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/assessment/start" className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold forr-btn-primary">Take the assessment <ArrowRight size={16} /></Link>
              <Link to="/coaching" className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold forr-btn-secondary">1-to-1 coaching <ArrowRight size={16} /></Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </PageShell>
);

export default AssessmentOverview;
